import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getArticleBySlug } from '../services/news';
import { Layout, Button } from '../components/common';
import 'highlight.js/styles/github.css';

interface ArticleData {
  metadata: {
    title: string;
    excerpt: string;
    author: string;
    date: string;
    category: string;
    tags: string[];
    featured?: boolean;
    readTime: string;
  };
  content: string;
}

const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    if (slug) {
      getArticleBySlug(slug)
        .then(setArticle)
        .catch(() => {
          setError(true);
        })
        .finally(() => setLoading(false));
    }
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[600px] flex items-center justify-center bg-Color-Scheme-1-Background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C97B5A] mx-auto"></div>
            <p className="mt-4 text-Color-Neutral-Dark font-cairo">Loading article...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <div className="min-h-[600px] flex items-center justify-center bg-Color-Scheme-1-Background">
          <div className="text-center">
            <h2 className="text-2xl font-bold font-gabarito text-Color-Scheme-1-Text mb-4">Article not found</h2>
            <p className="text-Color-Neutral-Dark mb-8 font-cairo">Sorry, we couldn't find the article you're looking for.</p>
            <Button onClick={() => navigate('/news')}>
              Back to News
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-white">
        {/* Back navigation */}
        <div className="border-b border-Color-Neutral-Light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/news" className="inline-flex items-center text-Color-Neutral-Dark hover:text-[#C97B5A] text-sm transition-colors font-cairo">
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span>Back to all posts</span>
          </Link>
        </div>
        </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold font-gabarito text-Color-Scheme-1-Text mb-6 leading-tight">
              {article.metadata.title}
            </h1>
            
            <div className="flex items-center gap-4 text-Color-Neutral-Dark font-cairo">
              <span className="font-medium">{article.metadata.author}</span>
              <span className="text-Color-Neutral-Medium">·</span>
              <time>{formatDate(article.metadata.date)}</time>
              <span className="text-Color-Neutral-Medium">·</span>
              <span>{article.metadata.readTime}</span>
            </div>
          </header>

          {/* Article Body */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-gabarito prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-Color-Scheme-1-Text
              prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-10
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:font-bold
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:font-bold
              prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2 prose-h4:font-bold
              prose-p:text-Color-Neutral-Dark prose-p:leading-[1.8] prose-p:mb-6 prose-p:text-[18px] prose-p:font-cairo
              prose-strong:text-Color-Scheme-1-Text prose-strong:font-semibold
              prose-em:text-Color-Neutral-Dark prose-em:italic
              prose-a:text-[#C97B5A] prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-[#B06747] prose-a:transition-colors
              prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
              prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6
              prose-li:text-Color-Neutral-Dark prose-li:leading-relaxed prose-li:mb-2 prose-li:font-cairo
              prose-blockquote:border-l-4 prose-blockquote:border-[#C97B5A] prose-blockquote:pl-5 prose-blockquote:my-8
              prose-blockquote:text-Color-Neutral-Dark prose-blockquote:italic
              prose-code:bg-Color-Neutral-Light prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-code:text-Color-Scheme-1-Text prose-code:font-mono prose-code:before:content-[''] prose-code:after:content-['']
              prose-pre:bg-Color-Neutral-Lightest prose-pre:border prose-pre:border-Color-Neutral-Medium prose-pre:overflow-x-auto prose-pre:rounded-lg
              prose-pre:p-4 prose-pre:my-8 prose-pre:text-sm
              [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-Color-Scheme-1-Text
              prose-table:my-8 prose-table:w-full prose-table:border-collapse
              prose-table:border prose-table:border-Color-Neutral-Medium
              prose-th:bg-Color-Neutral-Light prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold
              prose-th:border prose-th:border-Color-Neutral-Medium prose-th:text-Color-Scheme-1-Text
              prose-td:px-4 prose-td:py-2 prose-td:border prose-td:border-Color-Neutral-Medium
              prose-img:rounded-lg prose-img:my-8 prose-img:w-full
              prose-hr:my-10 prose-hr:border-Color-Neutral-Medium
              prose-figcaption:text-center prose-figcaption:text-sm prose-figcaption:text-Color-Neutral-Dark prose-figcaption:mt-2
              [&_details]:my-6 [&_details]:p-4 [&_details]:bg-Color-Neutral-Light [&_details]:rounded-lg [&_details]:border [&_details]:border-Color-Neutral-Medium
              [&_summary]:cursor-pointer [&_summary]:font-semibold [&_summary]:text-Color-Scheme-1-Text
              [&_del]:line-through [&_del]:text-Color-Neutral-Dark
              [&_mark]:bg-[#F5E6D3] [&_mark]:px-1 [&_mark]:rounded
              [&_kbd]:bg-white [&_kbd]:border [&_kbd]:border-Color-Neutral-Medium [&_kbd]:rounded [&_kbd]:px-2 [&_kbd]:py-0.5 [&_kbd]:text-sm [&_kbd]:font-mono [&_kbd]:shadow-sm
              [&_input[type='checkbox']]:mr-2 [&_input[type='checkbox']]:cursor-pointer
              [&_.task-list-item]:list-none [&_.task-list-item]:ml-0
              [&_.hljs]:bg-Color-Neutral-Lightest [&_.hljs]:border [&_.hljs]:border-Color-Neutral-Medium [&_.hljs]:overflow-x-auto [&_.hljs]:p-4 [&_.hljs]:rounded-lg
              [&_.hljs-comment]:text-Color-Neutral-Dark [&_.hljs-comment]:italic
              [&_.hljs-keyword]:text-[#B06747] [&_.hljs-built_in]:text-[#0891B2]
              [&_.hljs-string]:text-[#059669] [&_.hljs-number]:text-[#EA580C]
              [&_.hljs-function]:text-[#2563EB] [&_.hljs-title]:text-[#CA8A04]
              [&_.hljs-params]:text-Color-Neutral-Dark"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          {article.metadata.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-Color-Neutral-Light">
              <div className="flex items-center flex-wrap gap-2">
                {article.metadata.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-lg text-sm bg-Color-Neutral-Light text-Color-Neutral-Dark hover:bg-Color-Neutral-Medium transition-colors font-cairo"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Simplified Footer */}
      <footer className="mt-20 py-12 border-t border-Color-Neutral-Light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <Link to="/news" className="text-[#C97B5A] hover:text-[#B06747] font-medium transition-colors font-cairo">
              ← View all posts
            </Link>
            <Button variant="primary" size="sm">
              Subscribe to newsletter
            </Button>
          </div>
        </div>
      </footer>
      </div>
    </Layout>
  );
};

export default Article;